/**
 * File: readyToSendUtils.ts
 * Path: apps/admin/src/shared/utils/readyToSendUtils.ts
 * Description: The Ready to send list on Money and Today: every draft
 * invoice waiting for Liam (care plan months drafted a week before they are
 * due, and drafts saved by hand), soonest due first, with the reasons a
 * care plan month was held back from sending by itself.
 */

import { planHoldText, type InvoiceLedger, type SavedInvoice } from "@idk/config";

export interface ReadyRow {
  invoiceId: string;
  party: string;
  /** What it is for: the first line on the invoice. */
  forText: string;
  totalCents: number;
  dueOn: string;
  /** Care plan month, when it is one. */
  month: number | null;
  /** Why it waits for a person ("Month 13: the plan renews"…); empty when nothing holds it. */
  holds: string[];
}

export const readyToSend = (ledger: Pick<InvoiceLedger, "invoices">, saved: readonly SavedInvoice[]): ReadyRow[] => {
  const byId = new Map(saved.map((s) => [s.id, s]));
  return ledger.invoices
    .filter((r) => r.status === "draft" && r.invoiceId !== null)
    .map((r) => {
      const doc = byId.get(r.invoiceId as string);
      return {
        invoiceId: r.invoiceId as string,
        party: r.party,
        forText: doc?.draft.lines[0]?.description ?? "",
        totalCents: r.totalCents,
        dueOn: r.dueOn,
        month: doc?.carePlan?.month ?? null,
        holds: planHoldText(doc?.carePlan?.holds ?? []),
      };
    })
    .sort((a, b) => a.dueOn.localeCompare(b.dueOn) || a.party.localeCompare(b.party));
};
